// adminLogs.mjs — /admin/logs 的纯逻辑（第八十一批立）。
//
// ── 病灶 ──────────────────────────────────────────────────────────────────────
// dev 上已经有外包与顾问在真用，而"今天出了什么事"这个问题**在界面上没有答案**。
// 后端的能力自 WI-110 就齐了（RuntimeLog 的 Query + list_entries + list_restarts），
// 网关却从来没有过 logs 路由，前端自然也就没有过这一页——供给存在、可达性为零。
//
// ── 为什么这些判断必须是纯函数 ────────────────────────────────────────────────
// 日志页的失效方式不是"打不开"（那立刻会被发现），而是**打开了、但看到的是错的**：
//   · 级别选了 warn 却把 debug 也拉回来（或反过来漏掉那唯一一条 error）；
//   · datetime-local 的值当成 UTC 解析，于是"最近一小时"实际查的是八小时之前——
//     而页面上什么都不会说，只会显示一片空白，读起来像"这段时间很太平"；
//   · 翻页算错，最后一页永远点不到，而最旧的那几条恰恰是起因。
// 这三件事在界面上都长得像"没有日志"。所以判断全部落在这里，逐条穷举单测；
// 页面只负责渲染。
//
// ── 级别表为什么在这里"抄"一份 ────────────────────────────────────────────────
// 它不是抄的：adminLogs.test.mjs **真的去读** backend/src/core/runtime_log.h 的
// enum 定义逐条对账（做法沿用 helpContent.test.mjs 读 defaults.h）。后端改了数值
// 而这里没跟上，当场变红——而没有这条判据时，症状只是"筛选出来的行不太对"。

// 数值序有意义：配置的级别启用它**及其以下**的一切（none=0 全关）。
// 与 backend/src/core/runtime_log.h 的 `enum class LogLevel` 逐条对账。
export const LOG_LEVELS = [
  { name: 'error', value: 1 },
  { name: 'warn', value: 2 },
  { name: 'info', value: 3 },
  { name: 'debug', value: 4 },
];

// 页大小的默认与上限，与 backend/src/gateway/route_util.h 的
// kAdminLogsDefaultLimit / kAdminLogsMaxLimit 对账（测试逐条比）。
export const LOG_LIMIT_DEFAULT = 200;
export const LOG_LIMIT_MAX = 1000;
export const LOG_PAGE_SIZES = [50, 100, 200, 500, 1000];

const BY_NAME = new Map(LOG_LEVELS.map((l) => [l.name, l.value]));
const BY_VALUE = new Map(LOG_LEVELS.map((l) => [l.value, l.name]));

// 认不出来的级别回 ''（而不是 'error'）：把"参数认不出来"渲染成"只有错误"，
// 会让人盯着一页空白以为今天很太平——与后端 runtime_log_query_from 同一条口径。
export function levelName(value) {
  return BY_VALUE.get(Number(value)) ?? '';
}

export function levelValue(name) {
  return BY_NAME.get(String(name)) ?? 0;
}

// 级别 → MUI 颜色。debug 刻意用 'default'（灰）：把调试行也染成彩色，
// 满屏彩色等于没有强调。
export const LEVEL_COLOR = {
  error: 'error',
  warn: 'warning',
  info: 'info',
  debug: 'default',
};

export function levelColor(nameOrValue) {
  const n = typeof nameOrValue === 'number' ? levelName(nameOrValue) : String(nameOrValue ?? '');
  return LEVEL_COLOR[n] ?? 'default';
}

export function clampLimit(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return LOG_LIMIT_DEFAULT;
  return Math.min(Math.floor(v), LOG_LIMIT_MAX);
}

// ── 时间输入 ──────────────────────────────────────────────────────────────────
// <input type="datetime-local"> 给的是 "2026-08-05T14:30"，**没有时区**，
// 语义是"用户本地钟面上的这个时刻"。`new Date(s)` 对这种不带时区的串按本地时区解析
// （ISO 带 Z 或偏移时才是 UTC），所以这里就用它——但要显式写下这条约定，
// 因为"少一个 Z 就差八小时"正是这类过滤器最典型的静默错。
export function localInputToMs(value) {
  const s = String(value ?? '').trim();
  if (!s) return 0;
  const ms = new Date(s).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

// 反向：epoch ms → datetime-local 能吃的本地钟面串（不含秒）。
// 用 getFullYear 系列而不是 toISOString().slice()：后者是 UTC，会把输入框
// 里回填的时刻整体挪走时差那么多。
export function msToLocalInput(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '';
  const d = new Date(n);
  const p = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    + `T${p(d.getHours())}:${p(d.getMinutes())}`;
}

// ── 查询串 ────────────────────────────────────────────────────────────────────
// 过滤器 → 发给 /api/admin/logs 的参数对象。空值一律**不发**（不是发空串）：
// 一个 `category=` 在后端就是"精确匹配空类别"，能筛掉全部行。
export function buildLogQuery(f = {}) {
  const q = {};
  const from = localInputToMs(f.from);
  const to = localInputToMs(f.to);
  if (from > 0) q.fromMs = from;
  if (to > 0) q.toMs = to;
  if (f.maxLevel) q.maxLevel = String(f.maxLevel);
  if (f.category && String(f.category).trim()) q.category = String(f.category).trim();
  if (f.regex && String(f.regex).trim()) {
    q.regex = String(f.regex).trim();
    // icase 只在真有正则时才有意义；单独发一个 regexICase 只会让 URL 变长。
    if (f.regexICase) q.regexICase = 'true';
  }
  q.limit = clampLimit(f.limit);
  const offset = Number(f.offset);
  q.offset = Number.isFinite(offset) && offset > 0 ? Math.floor(offset) : 0;
  return q;
}

// 起止写反了是本地就能判定的错，不必往返一次服务端才知道。
// 返回 i18n 键（不是句子）——文案归词典，判据归这里。
export function filterProblems(f = {}) {
  const out = [];
  const from = localInputToMs(f.from);
  const to = localInputToMs(f.to);
  if (from > 0 && to > 0 && to < from) out.push('admin.logs.err.range');
  return out;
}

// ── 翻页 ──────────────────────────────────────────────────────────────────────
// total 是**过滤后、翻页前**的条数（后端如此投影）。页码从 1 起，给人看。
export function pageInfo(total, limit, offset) {
  const t = Math.max(0, Number(total) || 0);
  const l = clampLimit(limit);
  const o = Math.max(0, Number(offset) || 0);
  const pages = Math.max(1, Math.ceil(t / l));
  const page = Math.min(pages, Math.floor(o / l) + 1);
  return {
    page,
    pages,
    total: t,
    hasPrev: o > 0,
    // 判据用 offset+本页容量与 total 比，而不是 page<pages：offset 可能不是
    // limit 的整数倍（换了页大小之后），那时 page 的算法会骗人。
    hasNext: o + l < t,
    nextOffset: o + l,
    prevOffset: Math.max(0, o - l),
  };
}

// ── 重启锚点 ──────────────────────────────────────────────────────────────────
// 看日志时问的第一个问题是"这条报错是**这次**启动之后的吗"。重启标记是唯一
// 能回答它的东西（它不受级别闸约束，level=none 也照写）。这里把两条流合成
// 一条按时间倒序的时间线，页面据此在正确的位置画一道分隔线。
//
// 同一毫秒上重启排在日志**前面**（即渲染时更靠下）：重启先发生，之后那一毫秒
// 里产生的日志属于新一次启动。搞反的话，启动瞬间的报错会被归给上一次运行。
export function mergeTimeline(entries, restarts) {
  const rows = [
    ...(entries ?? []).map((e) => ({ kind: 'entry', tsMs: Number(e.tsMs) || 0, entry: e })),
    ...(restarts ?? []).map((r) => ({ kind: 'restart', tsMs: Number(r.tsMs) || 0, restart: r })),
  ];
  rows.sort((a, b) => {
    if (b.tsMs !== a.tsMs) return b.tsMs - a.tsMs;
    if (a.kind === b.kind) return 0;
    return a.kind === 'restart' ? 1 : -1;
  });
  return rows;
}

// 拉取重启标记的窗口：跟随当前结果的时间跨度，而不是另立一套。
// 结果为空时返回 null —— 没有行可锚，就别去取锚点。
export function restartWindow(entries) {
  const list = (entries ?? []).map((e) => Number(e.tsMs) || 0).filter((n) => n > 0);
  if (list.length === 0) return null;
  return { fromMs: Math.min(...list), toMs: Math.max(...list) };
}

// ── 被**交出**的站内信（第一百零九批 P1 ②：举报窄通道的管理面读取端）─────────
//
// 它落在这一页，是因为这两件事是同一件事的两半：出事那天有人来这里问"今天出了什么
// 事"，而"有人把一封信交上来了"正是那个答案的一部分。另开一页的代价不是多一页，
// 是多一个**没人会想起去看**的页面——而一条没人看的举报通道等于没有通道。
//
// ── 这只函数为什么是纯的 ───────────────────────────────────────────────────
// 它收敛的是一份**含正文**的报文，而这一页此前一个正文都不显示。三条判据：
//  · **正文缺席与正文为空必须可分辨**：后端给的 body 恒是字符串；一个 undefined
//    落到渲染器上会变成"—"，而"—"在这里读起来像"这封信没写什么"——那是一句关于
//    一件正在被举报的事的假话。缺席 ⇒ null（调用侧显示"取不到正文"），空串 ⇒ ''。
//  · **双方都必须具名**：一条只说"有人举报了一封信"的记录，管理员拿它什么也做不了。
//    任何一侧的显示名缺席 ⇒ 这一行整个丢掉，而不是渲染成"— → 张三"。
//  · **不排序**：顺序（reported_at_ms DESC，刚交上来的在前）是后端的展示语义，
//    前端再排一次就是第二个会漂的顺序（同 normalizeMessages 的那条纪律）。
export function normalizeReportedMessages(data) {
  const rows = Array.isArray(data?.reports) ? data.reports : [];
  const out = [];
  for (const r of rows) {
    if (!r || typeof r !== 'object' || r.id == null) continue;
    const from = typeof r.fromDisplay === 'string' ? r.fromDisplay : '';
    const to = typeof r.toDisplay === 'string' ? r.toDisplay : '';
    if (!from || !to) continue;
    out.push({
      id: String(r.id),
      fromDisplay: from,
      toDisplay: to,
      // SB-147：管理面并排邮箱（后端 require_admin 面新增两键；缺席 ⇒ ''，
      // partyLabel 退回只显名——部署错峰时旧响应不该把整行变成不可读）。
      fromEmail: typeof r.fromEmail === 'string' ? r.fromEmail : '',
      toEmail: typeof r.toEmail === 'string' ? r.toEmail : '',
      anchorKind: typeof r.anchorKind === 'string' ? r.anchorKind : '',
      anchorRef: typeof r.anchorRef === 'string' ? r.anchorRef : '',
      sentAtMs: msOrNull(r.sentAtMs),
      readAtMs: msOrNull(r.readAtMs),
      reportedAtMs: msOrNull(r.reportedAtMs),
      // 缺席 ⇒ null（"取不到正文"），空串 ⇒ ''（"这封信正文是空的"）。
      body: typeof r.body === 'string' ? r.body : null,
      bodyFormat: r.bodyFormat === 'plain' ? 'plain' : 'markdown',
    });
  }
  return out;
}

// SB-147：跨方具名的渲染形——「显示名 <邮箱>」；邮箱缺席时退回只显名（不渲染
// 空尖括号）。显示名是可变的装饰，邮箱才是身份——举报处置正是认错人代价最高处。
export function partyLabel(display, email) {
  return email ? `${display} <${email}>` : display;
}

// `Number(null)` 是 0，而 0 会在界面上印出 1970-01-01——一句确定的假话。
function msOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
